/**
 * The Emoji tab's inventory — exp-canvas-toolbar tray-stickers.jsx
 * `EMOJI_SECTIONS`, verbatim (ROUND3 §3, D39). Work comes first because it
 * is the section that earns the tray. Every item's `name` is the tooltip,
 * the accessible name and the only thing search matches.
 *
 * THE REGISTRY DOES NOT GOVERN THIS LIST: these characters are the user's
 * typing, rendered in the platform's own font, never a Lucide pairing.
 */

export interface EmojiEntry {
  key: string;
  char: string;
  name: string;
}

export interface EmojiSection {
  key: string;
  name: string;
  items: EmojiEntry[];
}

export const EMOJI_SECTIONS: EmojiSection[] = [
  {
    key: "work",
    name: "Work",
    items: [
      { key: "white-check-mark", char: "✅", name: "White check mark" },
      { key: "ballot-box-check", char: "☑️", name: "Ballot box with check" },
      { key: "check-mark", char: "✔️", name: "Check mark" },
      { key: "thumbs-up", char: "👍", name: "Thumbs up" },
      { key: "eyes", char: "👀", name: "Eyes" },
      { key: "raised-hand", char: "✋", name: "Raised hand" },
      { key: "rocket", char: "🚀", name: "Rocket" },
      { key: "fire", char: "🔥", name: "Fire" },
      { key: "hundred", char: "💯", name: "Hundred points" },
      { key: "star", char: "⭐", name: "Star" },
      { key: "sparkles", char: "✨", name: "Sparkles" },
      { key: "party-popper", char: "🎉", name: "Party popper" },
      { key: "clap", char: "👏", name: "Clapping hands" },
      { key: "handshake", char: "🤝", name: "Handshake" },
      { key: "folded-hands", char: "🙏", name: "Folded hands" },
      { key: "light-bulb", char: "💡", name: "Light bulb" },
      { key: "thinking-face", char: "🤔", name: "Thinking face" },
      { key: "chart-increasing", char: "📈", name: "Chart increasing" },
      { key: "pushpin", char: "📌", name: "Pushpin" },
      { key: "hourglass", char: "⏳", name: "Hourglass" },
      { key: "warning", char: "⚠️", name: "Warning" },
      { key: "prohibited", char: "🚫", name: "Prohibited" },
    ],
  },
  {
    key: "smileys",
    name: "Smileys",
    items: [
      { key: "grinning", char: "😀", name: "Grinning face" },
      { key: "smile", char: "😄", name: "Grinning face with smiling eyes" },
      { key: "sweat-smile", char: "😅", name: "Grinning face with sweat" },
      { key: "joy", char: "😂", name: "Face with tears of joy" },
      { key: "slight-smile", char: "🙂", name: "Slightly smiling face" },
      { key: "wink", char: "😉", name: "Winking face" },
      { key: "blush", char: "😊", name: "Smiling face with smiling eyes" },
      { key: "heart-eyes", char: "😍", name: "Smiling face with heart-eyes" },
      { key: "sunglasses", char: "😎", name: "Smiling face with sunglasses" },
      { key: "star-struck", char: "🤩", name: "Star-struck" },
      { key: "partying-face", char: "🥳", name: "Partying face" },
      { key: "saluting-face", char: "🫡", name: "Saluting face" },
      { key: "exploding-head", char: "🤯", name: "Exploding head" },
      { key: "grimacing", char: "😬", name: "Grimacing face" },
      { key: "melting-face", char: "🫠", name: "Melting face" },
      { key: "neutral-face", char: "😐", name: "Neutral face" },
      { key: "sleeping", char: "😴", name: "Sleeping face" },
      { key: "triumph", char: "😤", name: "Face with steam from nose" },
      { key: "sob", char: "😭", name: "Loudly crying face" },
      { key: "robot", char: "🤖", name: "Robot" },
    ],
  },
  {
    key: "people",
    name: "People",
    items: [
      { key: "wave", char: "👋", name: "Waving hand" },
      { key: "raising-hands", char: "🙌", name: "Raising hands" },
      { key: "raising-hand", char: "🙋", name: "Person raising hand" },
      { key: "muscle", char: "💪", name: "Flexed biceps" },
      { key: "heart-hands", char: "🫶", name: "Heart hands" },
      { key: "point-right", char: "👉", name: "Pointing right" },
      { key: "point-left", char: "👈", name: "Pointing left" },
      { key: "point-up", char: "👆", name: "Pointing up" },
      { key: "point-down", char: "👇", name: "Pointing down" },
      { key: "victory", char: "✌️", name: "Victory hand" },
      { key: "crossed-fingers", char: "🤞", name: "Crossed fingers" },
      { key: "call-me", char: "🤙", name: "Call me hand" },
      { key: "ok-hand", char: "👌", name: "OK hand" },
      { key: "writing-hand", char: "✍️", name: "Writing hand" },
      { key: "brain", char: "🧠", name: "Brain" },
      { key: "technologist", char: "🧑‍💻", name: "Technologist" },
      { key: "busts", char: "👥", name: "Busts in silhouette" },
      { key: "bust", char: "👤", name: "Bust in silhouette" },
    ],
  },
  {
    key: "objects",
    name: "Objects",
    items: [
      { key: "paperclip", char: "📎", name: "Paperclip" },
      { key: "file-folder", char: "📁", name: "File folder" },
      { key: "page-facing-up", char: "📄", name: "Page facing up" },
      { key: "memo", char: "📝", name: "Memo" },
      { key: "bar-chart", char: "📊", name: "Bar chart" },
      { key: "chart-decreasing", char: "📉", name: "Chart decreasing" },
      { key: "calendar", char: "🗓️", name: "Spiral calendar" },
      { key: "alarm-clock", char: "⏰", name: "Alarm clock" },
      { key: "locked", char: "🔒", name: "Locked" },
      { key: "key", char: "🔑", name: "Key" },
      { key: "wrench", char: "🔧", name: "Wrench" },
      { key: "hammer", char: "🔨", name: "Hammer" },
      { key: "test-tube", char: "🧪", name: "Test tube" },
      { key: "direct-hit", char: "🎯", name: "Direct hit" },
      { key: "trophy", char: "🏆", name: "Trophy" },
      { key: "gift", char: "🎁", name: "Wrapped gift" },
      { key: "coffee", char: "☕", name: "Hot beverage" },
      { key: "pizza", char: "🍕", name: "Pizza" },
      { key: "money-bag", char: "💰", name: "Money bag" },
      { key: "chain", char: "🔗", name: "Link" },
    ],
  },
  {
    key: "symbols",
    name: "Symbols",
    items: [
      { key: "red-heart", char: "❤️", name: "Red heart" },
      { key: "orange-heart", char: "🧡", name: "Orange heart" },
      { key: "yellow-heart", char: "💛", name: "Yellow heart" },
      { key: "green-heart", char: "💚", name: "Green heart" },
      { key: "blue-heart", char: "💙", name: "Blue heart" },
      { key: "purple-heart", char: "💜", name: "Purple heart" },
      { key: "exclamation", char: "❗", name: "Exclamation mark" },
      { key: "question", char: "❓", name: "Question mark" },
      { key: "double-bang", char: "‼️", name: "Double exclamation mark" },
      { key: "arrow-right", char: "➡️", name: "Right arrow" },
      { key: "arrow-left", char: "⬅️", name: "Left arrow" },
      { key: "arrow-up", char: "⬆️", name: "Up arrow" },
      { key: "arrow-down", char: "⬇️", name: "Down arrow" },
      { key: "red-circle", char: "🔴", name: "Red circle" },
      { key: "orange-circle", char: "🟠", name: "Orange circle" },
      { key: "yellow-circle", char: "🟡", name: "Yellow circle" },
      { key: "green-circle", char: "🟢", name: "Green circle" },
      { key: "blue-circle", char: "🔵", name: "Blue circle" },
      { key: "black-circle", char: "⚫", name: "Black circle" },
      { key: "recycle", char: "♻️", name: "Recycling symbol" },
    ],
  },
];

/** The platform's colour font: emoji are content, not iconography. */
export const EMOJI_FACE =
  "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";
